import React, { useEffect, ReactNode, useRef } from 'react';
import { X } from 'lucide-react';
import { Portal } from './Portal';
import { cn } from '@/shared/utils/cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
}

/**
 * Base modal dialog component with Portal-based rendering
 * Supports multiple sizes, backdrop click handling, and proper z-index layering
 * Implements focus trap for accessibility
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',
  closeOnBackdrop = true,
  showCloseButton = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = 'unset';
        // Restore focus to the previously active element
        previousActiveElement.current?.focus();
      };
    }
  }, [open]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // Focus trap - keep focus within modal
  useEffect(() => {
    if (open && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTabKey);
      // Focus first element when modal opens
      firstElement?.focus();

      return () => document.removeEventListener('keydown', handleTabKey);
    }
  }, [open]);

  if (!open) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnBackdrop) {
      onClose();
    }
  };

  const sizeClasses = {
    sm: 'md:max-w-md',
    md: 'md:max-w-2xl',
    lg: 'md:max-w-4xl',
    xl: 'md:max-w-6xl',
  };

  return (
    <Portal>
      {/* Backdrop - hidden on mobile since modal fills screen */}
      <div
        className="hidden md:block fixed inset-0 z-modal-backdrop bg-black/50 backdrop-blur-md"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal Container - z-index 300 on mobile (inline style), z-modal (25) on desktop */}
      <div
        className="fixed inset-0 md:z-modal flex items-center justify-center p-0 md:p-4 pointer-events-none"
        style={{ zIndex: window.innerWidth < 768 ? 300 : undefined }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div
          ref={modalRef}
          className={cn(
            'bg-gradient-to-br from-dark-bg via-dark-bg/98 to-dark-surface/90 rounded-none md:rounded-2xl border-0 md:border border-dark-border/30 shadow-2xl shadow-black/40',
            'w-full h-full md:h-auto md:max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto',
            'theme-dynamics:shadow-elevation-4',
            'theme-dynamics:md:rounded-large',
            sizeClasses[size]
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border/30 bg-gradient-to-r from-dark-surface/40 to-transparent">
            <h2
              id="modal-title"
              className="text-lg md:text-xl font-bold text-white tracking-tight"
            >
              {title}
            </h2>
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 hover:bg-dark-surface/60 rounded-lg transition-all duration-200 text-dark-muted hover:text-white"
                aria-label="Close modal"
                data-testid='unknown-close-btn'
                data-ux='close'
                data-interaction-id='unknown-close-btn'>
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Content */}
          <div
            className="flex-1 overflow-y-auto px-6 py-4 bg-transparent overscroll-contain"
            style={{
              touchAction: 'pan-y pinch-zoom',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </Portal>
  );
}
